import React from 'react';
import {
  UpdateShippingEstimateMutation,
  StartDigitalWalletCheckoutForListing,
  FinalizeDigitalWalletPayment,
  core_apimessages_Address as Address,
  core_apimessages_Money as Money,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
  core_apimessages_CheckoutLineItem_Type,
  Checkout,
} from '../schemas/rql';
import gql from 'graphql-tag';
import bind from '@reverbdotcom/commons/src/bind';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { MutationFn } from 'react-apollo';
import * as elog from '@reverbdotcom/commons/src/elog';
import ApplePay from '@adyen/adyen-web/dist/types/components/ApplePay';
import { withUserContext, IUserContext, IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { PaymentMethodIcons } from './payment_method_icons';
import { displayPaymentMethod } from '../lib/payment_method_icons';
import I18n from 'i18n-js';
import { adyenCheckout } from '../lib/adyen_checkout';
import { adyenEnvironment } from '../lib/adyenEnvironment';
import { countryCodeOrDefault, findMerchantId, paymentContactToAddressEntry } from './checkoutApplePay';
import CheckoutItemPageReturnLayout, { CheckoutItemPageReturnError, CheckoutItemPageReturnErrorCallback } from './checkout_item_page_return_layout';
import { reverbPaymentsPresentmentCurrency } from '@reverbdotcom/commons/src/currency';
import { connect } from '@reverbdotcom/commons/src/connect';

export const BuyListingNowApplePayFragment = gql`
  fragment BuyListingNowApplePayData on Listing {
    _id
    id
    currency
    adyenCheckoutPaymentMethodsConfig
  }
`;

interface ExternalProps {
  listingId: string;
  listingCurrencyCode: string;
  paymentMethodsConfig: string;
  setIsLoading: (loading: boolean) => any;
  isDisabled: boolean;
  onError: CheckoutItemPageReturnErrorCallback;
}

interface MutationProps {
  startDigitalWalletCheckoutForListing: MutationFn<StartDigitalWalletCheckoutForListing.Mutation, StartDigitalWalletCheckoutForListing.Variables>;
  updateCheckoutShippingEstimate: MutationFn<UpdateShippingEstimateMutation.Mutation, UpdateShippingEstimateMutation.Variables>;
  finalizeDigitalWalletPayment: MutationFn<FinalizeDigitalWalletPayment.Mutation, FinalizeDigitalWalletPayment.Variables>;
}

interface PaymentResult {
  /** Order Bundle ID */
  id?: string;
}

interface State {
  applePay?: ApplePay;
  totalAmount?: Money;
  paymentResult?: PaymentResult;
  checkoutId?: string;
  checkout: Checkout;
}

type Props = ExternalProps & MutationProps & IUserContext;

export class BuyListingNowApplePayButton extends React.Component<Props, State> {
  state = {
    applePay: undefined,
    totalAmount: undefined,
    paymentResult: undefined,
    checkoutId: undefined,
    checkout: undefined,
  };

  @bind
  async openPaymentSheet(e) {
    e.preventDefault();
    this.state.applePay.submit();
    const result = await this.props.startDigitalWalletCheckoutForListing({
      variables: {
        input: {
          listingId: this.props.listingId,
          paymentMethod: CheckoutPaymentMethodType.APPLE_PAY,
        },
      },
    });
    if (!result) {
      const err = new Error('checkout.apple_pay.fetch_line_items_error');
      elog.error(err.message, {}, err);
      return;
    }
    const checkoutId = result.data?.startCheckoutForListing?.checkoutId;
    this.setState({ checkoutId });
  }

  @bind
  async onShippingContactSelected(resolve, _reject, event) {
    const { locality, postalCode, administrativeArea, countryCode } = event.shippingContact;
    const shippingAddress: Address = {
      locality,
      postalCode,
      region: administrativeArea,
      countryCode: countryCode?.toUpperCase(),
    };

    const { checkoutId } = this.state;
    const input = {
      shippingAddress,
      id: checkoutId,
    };
    const result = await this.props.updateCheckoutShippingEstimate({
      variables: {
        input,
      },
    });
    if (!result) {
      const e = new Error('checkout.apple_pay.fetch_line_items_error');
      elog.error(e.message, {}, e);
      return;
    }
    const lineItems = result.data?.updateCheckoutShippingEstimate.checkout.lineItems;

    if (!lineItems) {
      const e = new Error('checkout.apple_pay.fetch_line_items_error');
      elog.error(e.message, {}, e);
      return;
    }

    const getLineItem = (lineItem): ApplePayJS.ApplePayLineItem => {
      return {
        amount: lineItem.amount.amount,
        label: lineItem.label,
        type: 'final',
      };
    };

    const total = lineItems.find(lineItem => lineItem.type === core_apimessages_CheckoutLineItem_Type.AMOUNT_OWED);
    const totalLineItem: ApplePayJS.ApplePayLineItem = getLineItem(total);
    this.setState({ totalAmount: total.amount });

    resolve({ newTotal: totalLineItem, newLineItems: lineItems.map((lineItem) => getLineItem(lineItem)) });
  }

  @bind
  async setCheckout(checkout) {
    this.setState(checkout);
  }

  @bind
  async onError(errorType: CheckoutItemPageReturnError, message: string) {
    this.setState({ paymentResult: null }, () => {
      this.props.onError(errorType, message);
    });
  }

  @bind
  async onAuthorized(resolve, _reject, event) {
    const { payment } = event;

    const guestUser = this.props.user.loggedOut ?
      {
        firstName: payment.shippingContact.givenName,
        lastName: payment.shippingContact.familyName,
        email: payment.shippingContact.emailAddress,
      } : null;

    const shippingAddress = paymentContactToAddressEntry(payment.shippingContact);
    const input = {
      id: this.state.checkoutId,
      totalPrice: {
        amountCents: this.state.totalAmount.amountCents,
        currency: this.state.totalAmount.currency,
      },
      paymentToken: btoa(JSON.stringify(payment.token.paymentData)),
      shippingAddress,
      guestUser,
    };

    try {
      const result = await this.props.finalizeDigitalWalletPayment({
        variables: {
          input,
        },
      });

      const paymentResult = result ? result.data?.finalizeDigitalWalletPayment : null;
      if (!paymentResult) {
        const paymentError = new Error('checkout.apple_pay.make_payment_error');
        elog.error('checkout.apple_pay.make_payment_error', {}, paymentError);
      }
      resolve();
      this.setState({ paymentResult });
    } catch (e) {
      elog.error('checkout.apple_pay.make_payment_error', {}, e);
    }
  }

  componentWillUnmount(): void {
    this.props.setIsLoading(false);
  }

  async componentDidMount() {
    const applePay = getApplePayConfiguration(
      this.props.user,
      this.props.paymentMethodsConfig,
      this.props.listingCurrencyCode,
      this.onShippingContactSelected,
      this.onAuthorized,
      Function, //onError, we handle this at the ReturnLayout layer
    );
    this.setState({ applePay });
  }

  render() {

    if (this.props.isDisabled) {
      return null;
    }

    const icons = displayPaymentMethod(CheckoutPaymentMethodType.APPLE_PAY)?.icons;

    return (
      <>
        <button
          type="button"
          onClick={async (e) => buildTrackAndOpenPaymentSheet({
            openPaymentSheet: this.openPaymentSheet(e),
            listingId: this.props.listingId,
            customTrackedValues: {
              userId: this.props.user.id,
              paymentMethod: CheckoutPaymentMethodType.APPLE_PAY,
            },
          })}
          className="payment-method-button"
          aria-label="Check out with Apple Pay"
        >
          <h3 className="payment-method-button__title">
            {I18n.t('discovery.checkout.buyItNow.applePay')}
          </h3>
          {icons &&
            <div className="payment-method-button__icons">
              <PaymentMethodIcons icons={icons} />
            </div>
          }
        </button>
        {this.state.paymentResult?.id &&
          <CheckoutItemPageReturnLayout
            checkoutId={this.state.checkoutId}
            onError={this.onError}
          />
        }
      </>
    );
  }
}

async function buildTrackAndOpenPaymentSheet({ openPaymentSheet, listingId, customTrackedValues }) {
  trackEvent({
    eventName: MParticleEventName.ClickedBuyItNowPaymentMethod,
    listingId,
    ...customTrackedValues,
  });
  return async function trackAndOpenPaymentSheet(event) {
    await openPaymentSheet(event);
  };
}

function getApplePayConfiguration(
  user: IUser,
  paymentMethodsConfig: string,
  listingCurrencyCode: string,
  onShippingContactSelected: any,
  onAuthorized: any,
  onError: any,
) {
  const paymentMethodsResponse = JSON.parse(paymentMethodsConfig);
  const merchantId = findMerchantId(paymentMethodsResponse);

  const configuration = {
    paymentMethodsResponse,
    clientKey: adyenEnvironment.clientKey,
    environment: adyenEnvironment.environment,
  };

  const checkout = adyenCheckout({ configuration });
  return checkout.create<'applepay'>('applepay', {
    countryCode: countryCodeOrDefault(user.countryCode),
    amount: zeroValueAmount(user.currency, listingCurrencyCode),
    totalPriceStatus: 'pending',
    requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
    onError,
    onShippingContactSelected,
    onAuthorized,
    configuration: {
      merchantId,
      merchantName: I18n.t('discovery.checkout.applePay.merchantName'),
    },
  });
}

function zeroValueAmount(buyerCurrencyCode: string, orderCurrencyCode: string) {
  return { value: 0, currency: reverbPaymentsPresentmentCurrency(buyerCurrencyCode, orderCurrencyCode) };
}

const startDigitalWalletCheckoutForListing = withGraphql<
  ExternalProps,
  StartDigitalWalletCheckoutForListing.Mutation,
  StartDigitalWalletCheckoutForListing.Variables
>(
  gql`
  mutation StartDigitalWalletCheckoutForListing($input: Input_core_apimessages_StartCheckoutForListingRequest) {
    startCheckoutForListing(input: $input) {
      checkoutId
    }
  }`,
  {
    name: 'startDigitalWalletCheckoutForListing',
  },
);

export const updateCheckoutShippingEstimate = withGraphql<
  ExternalProps,
  UpdateShippingEstimateMutation.Mutation,
  UpdateShippingEstimateMutation.Variables
>(
  gql`
    mutation UpdateShippingEstimateMutation(
      $input: Input_core_apimessages_CheckoutUpdateShippingEstimateRequest
    ) {
      updateCheckoutShippingEstimate(input: $input) {
        checkout {
          _id
          lineItems {
            type
            label
            amount {
              amount
              amountCents
              currency
              symbol
              display
            }
          }
        }
      }
    }
  `,
  {
    name: 'updateCheckoutShippingEstimate',
  },
);

const finalizeDigitalWalletPayment = withGraphql<
  ExternalProps,
  FinalizeDigitalWalletPayment.Mutation,
  FinalizeDigitalWalletPayment.Variables
>(
  gql`
  mutation FinalizeDigitalWalletPayment($input: Input_core_apimessages_CheckoutFinalizeDigitalWalletPaymentRequest) {
    finalizeDigitalWalletPayment(input: $input) {
      id
    }
  }`,
  {
    name: 'finalizeDigitalWalletPayment',
  },
);

export default connect<ExternalProps>([
  startDigitalWalletCheckoutForListing,
  updateCheckoutShippingEstimate,
  finalizeDigitalWalletPayment,
])(withUserContext(BuyListingNowApplePayButton));
